/**
 * Step timer — tracks active viewing time per step with idle detection,
 * visibility pausing, periodic flushing, and crash resilience.
 *
 * Emits phase-tagged entries for analytics:
 *  - `reading`   — step entry → first interaction (UPDATE_ANSWER)
 *  - `answering`  — first interaction → submit (Check button)
 *  - `reviewing`  — grading result shown → leave step
 *  - Info steps emit a single `reading` entry (no answering/reviewing phases).
 *
 * Also provides `getActiveTimeMs()` for the per-response `response_time_ms` metric
 * (total active time from step entry to submit, spanning reading + answering).
 *
 * Flush strategy:
 *  - On phase transition (previous phase's entry collected)
 *  - On step navigation (current phase's entry collected)
 *  - Every 60 seconds (crash resilience)
 *  - On tab hide (best-effort before potential close)
 *  - On session complete/exit (`stopAndFlush`)
 *  - On `beforeunload` and component unmount (best-effort via `fetch({ keepalive })`)
 */
import { useRef, useCallback, useEffect } from 'react';
import { API_BASE_URL } from '@/shared/api/client';
import type { StepTimeEntry } from '@formency/shared';
import { useIdleDetection } from './useIdleDetection';

// ─── Constants ─────────────────────────────────────────────────────────────

const FLUSH_INTERVAL_MS = 60 * 1000;
const MAX_PENDING_ENTRIES = 200;

// ─── Types ─────────────────────────────────────────────────────────────────

type StepPhase = 'reading' | 'answering' | 'reviewing';

interface TimerState {
  stepId: string;
  visitNumber: number;
  phase: StepPhase;
  startedAt: string;
  accumulatedMs: number;
  lastResumeAt: number;
  paused: boolean;
  /** Cumulative active ms from step entry through end of answering (for response_time_ms). */
  responseAccMs: number;
  /** Cumulative reading phase ms (for reading_ms metric). */
  readingAccMs: number;
  /** Cumulative answering phase ms (for answering_ms metric). */
  answeringAccMs: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

/** Build a step-time flush URL for the given session. */
function buildFlushUrl(sessionId: string): string {
  return `${API_BASE_URL.replace(/\/+$/, '')}/me/study-sessions/${sessionId}/step-time`;
}

/**
 * Fire-and-forget flush using `fetch({ keepalive: true })`.
 * Used for all flush calls — periodic, visibility, beforeunload, unmount.
 * Raw fetch is required because TanStack mutations can't run outside React
 * and `keepalive` is needed for tab-close scenarios.
 */
function flushEntriesSync(sessionId: string, entries: StepTimeEntry[]): void {
  if (entries.length === 0) return;
  // BetterAuth HttpOnly cookies are sent automatically; no Authorization
  // header needed. credentials: 'include' is REQUIRED because step-time
  // flushes target api.formency.app from formency.app in production —
  // cross-subdomain. keepalive: true preserves the request across
  // tab-close/unload events.
  fetch(buildFlushUrl(sessionId), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ entries }),
    keepalive: true,
  })
    .then((r) => {
      // 401 here means the cookie expired mid-session. The axios client
      // path bounces to /login via handleSessionExpired; mirror that here
      // so the user isn't stranded on a stale session-player view. No
      // toast — handleSessionExpired owns its own UX.
      if (r.status === 401) {
        // Lazy-import to avoid a circular dep with the api/ folder during
        // hooks/ module init.
        void Promise.all([
          import('@/shared/api/sessionExpired'),
          import('@/shared/api/signOutFlag'),
        ]).then(([{ handleSessionExpired }, { isSigningOut }]) => {
          if (!isSigningOut()) handleSessionExpired();
        });
      }
    })
    .catch(() => {
      // Network error or aborted unload — best-effort flush, don't escalate.
    });
}

// ─── Hook ──────────────────────────────────────────────────────────────────

export function useStepTimer(sessionId: string) {
  const timerRef = useRef<TimerState | null>(null);
  const pendingRef = useRef<StepTimeEntry[]>([]);
  const visitCountsRef = useRef<Map<string, number>>(new Map());
  const flushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionIdRef = useRef(sessionId);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // ── Timer primitives ───────────────────────────────────────────────────

  const getActiveDuration = useCallback((): number => {
    const t = timerRef.current;
    if (!t) return 0;
    if (t.paused) return t.accumulatedMs;
    return t.accumulatedMs + (performance.now() - t.lastResumeAt);
  }, []);

  const pause = useCallback(() => {
    const t = timerRef.current;
    if (!t || t.paused) return;
    t.accumulatedMs += performance.now() - t.lastResumeAt;
    t.paused = true;
  }, []);

  const resume = useCallback(() => {
    const t = timerRef.current;
    if (!t || !t.paused) return;
    t.lastResumeAt = performance.now();
    t.paused = false;
  }, []);

  // ── Entry collection ───────────────────────────────────────────────────

  /**
   * Snapshot the current timer into a StepTimeEntry and reset its accumulator.
   * Returns null if there's no active timer or duration is zero.
   */
  const collectAndReset = useCallback((): StepTimeEntry | null => {
    const t = timerRef.current;
    if (!t) return null;

    const durationMs = Math.round(getActiveDuration());
    if (durationMs <= 0) return null;

    const entry: StepTimeEntry = {
      stepId: t.stepId,
      durationMs,
      phase: t.phase,
      startedAt: t.startedAt,
      endedAt: new Date().toISOString(),
      visitNumber: t.visitNumber,
      clientEntryId: crypto.randomUUID(),
    };

    // Accumulate into phase-specific counters before resetting
    if (t.phase === 'reading') {
      t.responseAccMs += durationMs;
      t.readingAccMs += durationMs;
    } else if (t.phase === 'answering') {
      t.responseAccMs += durationMs;
      t.answeringAccMs += durationMs;
    }

    // Reset accumulator for the next collection interval
    const wasPaused = t.paused;
    t.accumulatedMs = 0;
    t.lastResumeAt = performance.now();
    t.startedAt = new Date().toISOString();
    t.paused = wasPaused;

    return entry;
  }, [getActiveDuration]);

  // ── Flushing ───────────────────────────────────────────────────────────

  /** Add entry to pending queue, respecting the size cap. */
  const enqueue = useCallback((entry: StepTimeEntry) => {
    // Drop oldest entries if we've hit the cap (server is probably unreachable)
    if (pendingRef.current.length >= MAX_PENDING_ENTRIES) {
      pendingRef.current = pendingRef.current.slice(-Math.floor(MAX_PENDING_ENTRIES / 2));
    }
    pendingRef.current.push(entry);
  }, []);

  /** Flush pending entries to the server (best-effort, fire-and-forget). */
  const flushToServer = useCallback(() => {
    if (pendingRef.current.length === 0) return;
    const entries = [...pendingRef.current];
    pendingRef.current = [];
    flushEntriesSync(sessionIdRef.current, entries);
  }, []);

  // ── Idle detection ─────────────────────────────────────────────────────

  const { resetIdleTimer } = useIdleDetection({
    onIdle: pause,
    onActive: () => {
      if (!document.hidden) resume();
    },
  });

  // ── Phase transitions ─────────────────────────────────────────────────

  /**
   * Transition from reading → answering phase.
   * Call when the learner first interacts with the step (UPDATE_ANSWER dispatch).
   * Collects the reading entry and starts the answering timer.
   */
  const markInteraction = useCallback(() => {
    const t = timerRef.current;
    if (!t || t.phase !== 'reading') return;

    const entry = collectAndReset();
    if (entry) enqueue(entry);
    t.phase = 'answering';
  }, [collectAndReset, enqueue]);

  /**
   * Transition from answering → reviewing phase.
   * Call when the grading result is received (RECEIVE_RESULT dispatch).
   * Collects the answering entry and starts the reviewing timer.
   */
  const markGraded = useCallback(() => {
    const t = timerRef.current;
    if (!t || t.phase !== 'answering') return;

    const entry = collectAndReset();
    if (entry) enqueue(entry);
    t.phase = 'reviewing';
  }, [collectAndReset, enqueue]);

  // ── Public API ─────────────────────────────────────────────────────────

  /** Start tracking a new step. Collects the previous step's entry if any. */
  const startStep = useCallback(
    (stepId: string, initialPhase: StepPhase = 'reading') => {
      const prev = collectAndReset();
      if (prev) enqueue(prev);

      const prevCount = visitCountsRef.current.get(stepId) ?? 0;
      const visitNumber = prevCount + 1;
      visitCountsRef.current.set(stepId, visitNumber);

      timerRef.current = {
        stepId,
        visitNumber,
        phase: initialPhase,
        startedAt: new Date().toISOString(),
        accumulatedMs: 0,
        lastResumeAt: performance.now(),
        paused: false,
        responseAccMs: 0,
        readingAccMs: 0,
        answeringAccMs: 0,
      };

      resetIdleTimer();
    },
    [collectAndReset, enqueue, resetIdleTimer]
  );

  /** Stop tracking and flush everything. Call on session complete or exit. */
  const stopAndFlush = useCallback(() => {
    const entry = collectAndReset();
    if (entry) enqueue(entry);
    flushToServer();
    timerRef.current = null;
  }, [collectAndReset, enqueue, flushToServer]);

  /**
   * Get total active time from step entry through current moment (reading + answering).
   * Used as response_time_ms on response submission.
   */
  const getActiveTimeMs = useCallback((): number => {
    const t = timerRef.current;
    if (!t) return 0;
    // responseAccMs holds completed phases; add current phase's live duration
    return Math.round(t.responseAccMs + getActiveDuration());
  }, [getActiveDuration]);

  /**
   * Get reading and answering phase times separately.
   * Used for behavioral tracking metrics on response submission.
   */
  const getPhaseTimesMs = useCallback((): { readingMs: number; answeringMs: number } => {
    const t = timerRef.current;
    if (!t) return { readingMs: 0, answeringMs: 0 };

    // If currently in reading or answering, add live duration to that phase
    const liveDuration = getActiveDuration();
    let readingMs = t.readingAccMs;
    let answeringMs = t.answeringAccMs;

    if (t.phase === 'reading') {
      readingMs += liveDuration;
    } else if (t.phase === 'answering') {
      answeringMs += liveDuration;
    }

    return { readingMs: Math.round(readingMs), answeringMs: Math.round(answeringMs) };
  }, [getActiveDuration]);

  // ── Event listeners ────────────────────────────────────────────────────

  // Visibility: pause on hide (+ flush for crash resilience), resume on show
  useEffect(() => {
    const handler = () => {
      if (document.hidden) {
        pause();
        const entry = collectAndReset();
        if (entry) enqueue(entry);
        flushToServer();
      } else {
        resume();
        resetIdleTimer();
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [pause, resume, resetIdleTimer, collectAndReset, enqueue, flushToServer]);

  // Periodic flush (crash resilience)
  useEffect(() => {
    flushIntervalRef.current = setInterval(() => {
      const entry = collectAndReset();
      if (entry) enqueue(entry);
      flushToServer();
    }, FLUSH_INTERVAL_MS);

    return () => {
      if (flushIntervalRef.current) clearInterval(flushIntervalRef.current);
    };
  }, [collectAndReset, enqueue, flushToServer]);

  // beforeunload: best-effort synchronous flush
  useEffect(() => {
    const handler = () => {
      const entry = collectAndReset();
      if (entry) pendingRef.current.push(entry);
      if (pendingRef.current.length > 0) {
        flushEntriesSync(sessionIdRef.current, pendingRef.current);
        pendingRef.current = [];
      }
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [collectAndReset]);

  // Unmount: best-effort flush (SPA navigation away from session)
  useEffect(() => {
    return () => {
      if (flushIntervalRef.current) clearInterval(flushIntervalRef.current);
      const entry = collectAndReset();
      if (entry) pendingRef.current.push(entry);
      if (pendingRef.current.length > 0) {
        flushEntriesSync(sessionIdRef.current, pendingRef.current);
        pendingRef.current = [];
      }
    };
  }, [collectAndReset]);

  return { startStep, stopAndFlush, getActiveTimeMs, getPhaseTimesMs, markInteraction, markGraded };
}
